/**
 * The ceilings, and the refusals that carry their reasons (#1069 Story 4.1,
 * Task 3, AC 4).
 *
 * ⚠️ A REFUSAL IS NOT A DROP, AND THE SIGNATURE IS SHAPED TO MAKE THAT HARD TO
 * GET WRONG. AC 4 requires that every refusal states its reason and that the
 * already-staged files survive, and it forbids silently truncating a
 * multi-select. So `admitFiles` does not return "the files that fit": it
 * returns what was accepted AND what was refused, with a reason per refusal.
 * A function that returned only the survivors could not satisfy AC 4 however
 * careful its internals were, because the caller would have nothing to show.
 *
 * ⚠️ THE NUMBERS ARE THE OWNER'S (D1, amended 2026-09-13): 6 files, 25 MB each,
 * 40 MB per message. The per-file ceiling sits BELOW the message ceiling on
 * purpose, so one 25 MB file is legal. `STAGED_LIMITS_HINT` in
 * `StagedFileStrip.tsx` states these same three numbers to the user; if a
 * ceiling moves, both move or the composer is lying to them.
 *
 * ⚠️ THE SERVER RE-CHECKS ALL OF THIS (AC 7). This is the courteous half: it
 * refuses at selection so nobody waits for a 40 MB upload to be told no. It is
 * not the control.
 */

import {
  MAX_MESSAGE_RAW_CHARS,
  MAX_RAW_PAYLOAD_CHARS_PER_FILE,
} from './messageFileBytes';
import {
  getBlockedFileErrorMessage,
  isBlockedExecutable,
} from '../../constants/security';

import type { StagedFile } from './stagedFile';

const MB = 1024 * 1024;

/** D1: at most six files ride on one message. */
export const MAX_FILES_PER_MESSAGE = 6;
/** D1 as amended: 25 MB per file, DECODED (D1a: the user's own file). */
export const MAX_PER_FILE_BYTES = 25 * MB;
/** D1: 40 MB of decoded bytes across the whole message. */
export const MAX_MESSAGE_BYTES = 40 * MB;
/**
 * Story 7.5, D5: the ceiling on one conversation zip's decoded bytes, five
 * messages at the message ceiling (200 MB). Mirrors the server's
 * `MAX_ZIP_BYTES` in `correspondenceFileLimits.ts` and is held equal by the
 * parity test in `messageFileBytes.test.ts`. The client refuses a "Save all"
 * it can already see is over this WITHOUT sending the request (AC 15): the
 * listing's `sizeBytes` are the decoded sizes the server sums.
 */
export const MAX_ZIP_BYTES = 5 * MAX_MESSAGE_BYTES;

/**
 * Story 8.3, D1: the ceiling on the DECODED bytes the viewer's cache may hold
 * for ONE open conversation (`messageFileCache.ts`, owned by
 * `useMessageFileCache`). Two messages' worth: one bubble's gallery and the
 * next bubble's both fit, so a reader can close one gallery, open the
 * neighbour's, and come back without downloading either again (C5 -- the set
 * is one bubble's images since Story 7.6, so the second half buys exactly
 * that and nothing within a gallery, which one message's worth covers).
 *
 * ⚠️ THE RESIDENT COST IS 4/3 OF THIS IN BASE64 CHARACTERS, expected around
 * 107 MB at the ceiling (one byte per ASCII character, the reading the walk's
 * gauge settles), plus the on-screen file's `data:` URL copy in the viewer, and it
 * sits BESIDE `MAX_STAGED_BYTES_ALL_THREADS` (the composer's own 40 MB pool):
 * the panel can now hold both at once. The next budget added to this panel
 * is added with that sum in mind.
 *
 * ⚠️ NO RAW-CHARS TWIN, ON PURPOSE (C4). Every other ceiling here carries a
 * second, raw bound because base64 that ARRIVES from a sender can be padded
 * with free whitespace. The cache's base64 is produced by our own `toBase64`
 * from decoded bytes in an exact 4:3 ratio, so one number bounds both. Do
 * not add one; do not measure it with `decodedBase64ByteLength` either (that
 * is a regex pass over a 33 MB string): `FetchedMessageFile.byteLength` is
 * read from the response for free.
 *
 * ⚠️ NO SERVER TWIN. This is a client memory budget, not a wire rule, so the
 * parity test in `messageFileBytes.test.ts` does not know it and must not.
 *
 * The `2` is the epic's recommendation, not a measurement: the Epic 8 walk
 * records the Xcode memory gauge with the cache full, and the owner decides
 * with that number in hand. If the answer is one message's worth, the change
 * is the `2` and nothing else.
 */
export const MESSAGE_FILE_CACHE_BYTES = 2 * MAX_MESSAGE_BYTES;

/**
 * The ceiling as a PREDICATE, compared with the server's on the boundary by
 * `zipFileName.test.ts` (the predicates, not the constants, per
 * `.claude/rules/correspondence.md`). Exactly `MAX_ZIP_BYTES` is sent; one
 * byte more is refused here, before any request.
 */
export const isZipTooLarge = (totalBytes: number): boolean =>
  totalBytes > MAX_ZIP_BYTES;

/** Which rule turned a file away. Carried so the caller can group or count. */
export type RefusalRule =
  | 'type'
  | 'per-file'
  | 'raw'
  | 'count'
  | 'total'
  | 'raw-total';

export interface FileRefusal {
  name: string;
  reason: string;
  rule: RefusalRule;
}

export interface Admission {
  /** The NEW files that may be staged. The caller appends; it never replaces. */
  accepted: StagedFile[];
  /** One per refused file, in selection order, each with its reason. */
  refusals: FileRefusal[];
}

const sumBytes = (files: readonly StagedFile[]): number =>
  files.reduce((total, f) => total + f.sizeBytes, 0);

const sumRaw = (files: readonly StagedFile[]): number =>
  files.reduce((total, f) => total + f.rawChars, 0);

/**
 * Active content, refused at selection because the server refuses it on arrival
 * (code review run 2, 2026-09-13).
 *
 * ⚠️ THE SERVER GREW THIS REFUSAL IN RUN 1 AND THE CLIENT DID NOT, so a 24 MB
 * SVG staged, uploaded in full, and was refused after the wait — the punishment
 * D1 forbids, introduced by the same pass that added the executable check to
 * prevent it. Kept in step with `ACTIVE_CONTENT_EXTENSIONS` in
 * `server/services/correspondenceFileTypes.ts`; the parity test holds them
 * equal, because a client that refuses LESS is a wasted upload and one that
 * refuses MORE silently forbids a file the product allows.
 */
export const ACTIVE_CONTENT_EXTENSIONS: readonly string[] = [
  'html',
  'htm',
  'xhtml',
  'shtml',
  'svg',
  'svgz',
];

/**
 * Mirrors the server's `extensionOf` — and the mirror is the point, so read
 * that one's comment before touching this.
 *
 * ⚠️ NORMALISE, THEN SPLIT (code review run 4, 2026-09-14). A trailing dot or a
 * zero-width character made this return `''`, which is in no refusal set, so
 * `logo.svg.` was staged, uploaded and stored — on both sides, since the server
 * had the identical hole. Windows strips the trailing dot on save and the file
 * lands as `logo.svg`.
 */
const ZERO_WIDTH = /[\u200B-\u200D\uFEFF]/g;
const TRAILING_JUNK = /[\s.]+$/;

export const extensionOf = (fileName: string): string => {
  const normalised = fileName
    .replace(ZERO_WIDTH, '')
    .toLowerCase()
    .trim()
    .replace(TRAILING_JUNK, '');
  const dot = normalised.lastIndexOf('.');
  return dot > 0 ? normalised.slice(dot + 1).replace(TRAILING_JUNK, '') : '';
};

/**
 * The name with the same normalisation applied, for the checks that take a
 * whole NAME rather than an extension.
 *
 * ⚠️ `isBlockedExecutable` (`src/constants/security.ts`) does its own
 * `lastIndexOf('.')` with no trim at all, so `"virus.exe "` is not blocked by
 * extension on either side (code review run 4). That function is the shared
 * attachments path and is deferred rather than changed here — but nothing stops
 * THIS call site handing it a name it can read.
 */
const normalisedName = (fileName: string): string =>
  fileName.replace(ZERO_WIDTH, '').trim().replace(TRAILING_JUNK, '');

/**
 * The claims the server refuses outright. Mirrors `ACTIVE_CONTENT_MIMES` in
 * `server/services/correspondenceFileTypes.ts`.
 */
export const ACTIVE_CONTENT_MIME_TYPES: readonly string[] = [
  'text/html',
  'application/xhtml+xml',
  'image/svg+xml',
];

/**
 * The refusal sentence, EXPORTED so the parity test can hold it equal to the
 * server's `ACTIVE_CONTENT_REFUSAL` (code review run 3, 2026-09-13). The
 * extension list was derived and parity-tested and the sentence beside it was a
 * hand-typed second spelling — revise the server wording and the same refusal
 * reads two different ways depending on which side it fired on.
 */
export const ACTIVE_CONTENT_REFUSAL =
  'Web pages and SVG images cannot be attached to a message.';

/**
 * ⚠️ BOTH HALVES, LIKE THE SERVER (code review run 3, 2026-09-13). This was
 * `isActiveContentName` and checked the EXTENSION only, while the server
 * refuses on `ACTIVE_CONTENT_MIMES.has(claimed)` OR the extension. A file the
 * platform reports as `text/html` or `image/svg+xml` whose name carries no
 * matching extension — an Android `content://` pick named `document`, an iOS
 * share with no extension — staged, uploaded IN FULL, and was refused only on
 * arrival: the punishment D1 forbids, in the block added to prevent it. The
 * parity test compared the two extension LISTS and was structurally blind to
 * the asymmetry, so it now compares the predicate.
 */
export const isActiveContent = (file: {
  name: string;
  mimeType: string;
}): boolean =>
  ACTIVE_CONTENT_MIME_TYPES.includes(
    file.mimeType.toLowerCase().split(';')[0].trim(),
  ) || ACTIVE_CONTENT_EXTENSIONS.includes(extensionOf(file.name));

/**
 * The ceiling on everything staged ACROSS threads, not within one.
 *
 * ⚠️ THE PER-THREAD MAP HAD NO AGGREGATE BOUND AT ALL (code review run 3,
 * 2026-09-13, owner decision the same day). `admitFiles` caps 6 files / 40 MB
 * PER THREAD, and the map the panel keys by thread was never purged: not on
 * leaving a thread, not on switching space tabs, not on the D3 access-revoked
 * path, not when the thread was deleted. Stage in three threads and the phone
 * retained up to 120 MB of base64. The global array this replaced was bounded
 * by construction, so the keying that fixed the audience leak removed the
 * bound — the same shape as run 2's own regression, one level up.
 *
 * Set to the per-message ceiling, so the message being composed ALWAYS fits
 * and only strips the user walked away from are ever at risk.
 */
export const MAX_STAGED_BYTES_ALL_THREADS = MAX_MESSAGE_BYTES;

/**
 * And the RAW ceiling for the same pool.
 *
 * ⚠️ THE DECODED NUMBER IS THE WRONG ONE TO BOUND MEMORY WITH, AND THIS MODULE
 * SAYS SO TWICE ELSEWHERE (code review run 4, 2026-09-14). `admitFiles` checks
 * `rawChars` BEFORE `sizeBytes` precisely because a padded payload is the case
 * where the decoded number is reassuring and wrong, and `MAX_MESSAGE_RAW_CHARS`
 * exists on both client and server for the same reason. `capStagedThreads`
 * shipped counting decoded bytes only — in a function whose entire stated
 * purpose is "how much base64 is this phone holding". Six strips of 1 MB
 * decoded / 54 MB raw each would pass a 6 MB "total" while retaining 324 MB.
 * Even honest clients make the cap loose: 40 MB decoded is ~53 MB of base64 and
 * ~107 MB of UTF-16 string. Both numbers, or the cap measures the wrong thing.
 */
export const MAX_STAGED_RAW_CHARS_ALL_THREADS = MAX_MESSAGE_RAW_CHARS;

/**
 * Evict the least-recently-touched threads' strips until the total fits.
 *
 * ⚠️ ORDER IS THE `Map`'S, WHICH IS WHY THE PANEL HOLDS A `Map` (owner option
 * (b), 2026-09-13). The caller deletes and re-sets the thread it just wrote, so
 * the most-recently-touched is always last and is never a candidate: purging on
 * leaving a thread was the simpler option and was rejected because it breaks
 * AC 12 — leave mid-upload, have the post FAIL, and there are no files left to
 * hand back.
 */
export const capStagedThreads = (
  byThread: Map<string, readonly StagedFile[]>,
): Map<string, readonly StagedFile[]> => {
  let bytes = 0;
  let raw = 0;
  for (const files of byThread.values()) {
    bytes += sumBytes(files);
    raw += sumRaw(files);
  }
  const overCeiling = () =>
    bytes > MAX_STAGED_BYTES_ALL_THREADS ||
    raw > MAX_STAGED_RAW_CHARS_ALL_THREADS;
  if (!overCeiling()) return byThread;

  const capped = new Map(byThread);
  // Oldest first, and never the last entry: that is the strip in front of the
  // user right now.
  for (const [threadId, files] of byThread) {
    if (capped.size <= 1 || !overCeiling()) break;
    capped.delete(threadId);
    bytes -= sumBytes(files);
    raw -= sumRaw(files);
  }
  return capped;
};

/**
 * Decide which of `incoming` may join `already`.
 *
 * Evaluated file by file IN ORDER, and the running total includes files
 * admitted earlier in this same call: three 20 MB files must not all pass
 * because each was measured against the original total.
 */
export const admitFiles = (
  already: readonly StagedFile[],
  incoming: StagedFile[],
): Admission => {
  const accepted: StagedFile[] = [];
  const refusals: FileRefusal[] = [];

  let count = already.length;
  let bytes = sumBytes(already);
  let raw = sumRaw(already);

  for (const file of incoming) {
    // ⚠️ TYPE FIRST, AND IT WAS MISSING ENTIRELY (AC 4 bullet 4, code review
    // 2026-09-13). There were four rules and none of them was type, so a
    // `.exe` staged happily, uploaded in full, and was refused by the server
    // only after the wait — which is the outcome D1 calls "not a limit, a
    // punishment". It runs before the size rules because a blocked type is
    // refused whether it is 2 KB or 24 MB, and the reason should say which.
    //
    // ⚠️ THE PRODUCT'S EXISTING WORDING, NOT A NEW STRING, as AC 4 requires:
    // `getBlockedFileErrorMessage` is the same function the request-attachment
    // path uses, so a `.exe` is refused in the same words wherever it is
    // offered.
    if (isBlockedExecutable(normalisedName(file.name))) {
      refusals.push({
        name: file.name,
        reason: getBlockedFileErrorMessage(file.name),
        rule: 'type',
      });
      continue;
    }

    // The server's other type refusal, mirrored so it costs nobody an upload.
    if (isActiveContent(file)) {
      refusals.push({
        name: file.name,
        reason: ACTIVE_CONTENT_REFUSAL,
        rule: 'type',
      });
      continue;
    }

    if (file.sizeBytes > MAX_PER_FILE_BYTES) {
      refusals.push({
        name: file.name,
        reason: `${file.name} is too large. Files are limited to 25 MB each.`,
        rule: 'per-file',
      });
      continue;
    }

    // D1a's SECOND check, and the reason `rawChars` exists. The decoded size
    // above strips whitespace before measuring, so a 1 MB image padded with
    // tens of megabytes of newlines passes it while every padding byte is
    // still stored. Same message as the per-file refusal: to the person who
    // chose it, this IS a file that is too large.
    if (file.rawChars > MAX_RAW_PAYLOAD_CHARS_PER_FILE) {
      refusals.push({
        name: file.name,
        reason: `${file.name} is too large. Files are limited to 25 MB each.`,
        rule: 'raw',
      });
      continue;
    }

    if (count >= MAX_FILES_PER_MESSAGE) {
      refusals.push({
        name: file.name,
        reason:
          'Up to 6 files per message. Attach more to the request itself for larger sets.',
        rule: 'count',
      });
      continue;
    }

    if (bytes + file.sizeBytes > MAX_MESSAGE_BYTES) {
      refusals.push({
        name: file.name,
        reason:
          'That would take this message over 40 MB. Remove a file, or send them in two messages.',
        rule: 'total',
      });
      continue;
    }

    // ⚠️ THE MESSAGE'S RAW TOTAL, the client half of the server ceiling added
    // in run 1. Six files that are mostly padding pass every rule above and are
    // 144 MB on the wire; without this the person waits for all of it to be
    // told no. Same sentence as the server's, for the same reason the per-file
    // raw refusal reuses the per-file one: to the user this is simply too much
    // to send.
    if (raw + file.rawChars > MAX_MESSAGE_RAW_CHARS) {
      refusals.push({
        name: file.name,
        reason:
          'That would take this message over 40 MB of upload. Remove a file, or send them in two messages.',
        rule: 'raw-total',
      });
      continue;
    }

    accepted.push(file);
    count += 1;
    bytes += file.sizeBytes;
    raw += file.rawChars;
  }

  return { accepted, refusals };
};
